import { format } from "date-fns";
import {
  createAttendance,
  getTodaysAttendanceByDoctorId,
  getTodaysAttendanceByStaffId,
  updateAttendance,
} from "../services/attendanceService";
import { getDoctorByUserId, getDoctorByMobile } from "../services/doctorService";
import { getStaffByUserId, getStaffByMobile } from "../services/StaffService";
import subscriptionHistoryService from "../services/subscriptionHistoryService";
import { getOrganizationId } from "./organizationUtils";

export const normalizeRoleName = (roleName) =>
  String(roleName || "")
    .toLowerCase()
    .trim()
    .replace(/[\s-]+/g, "_");

const DOCTOR_TABLE_ROLES = ["doctor", "surgeon", "anesthesiologist"];

const STAFF_TABLE_ROLES = [
  "staff",
  "admin",
  "superadmin",
  "super_admin",
  "pathdesk",
  "path_admin",
  "pathology_admin",
  "frontdesk",
  "ot_nurse",
  "circulating_ot_nurse",
  "ot_technician",
  "ot_attendant",
  "pharmacy_admin",
];

export const isDoctorTableRole = (roleName) =>
  DOCTOR_TABLE_ROLES.includes(normalizeRoleName(roleName));

export const isStaffTableRole = (roleName) =>
  STAFF_TABLE_ROLES.includes(normalizeRoleName(roleName));

export const shouldTrackAttendance = (roleName) => {
  const normalized = normalizeRoleName(roleName);
  if (normalized === "patient") return false;
  return isDoctorTableRole(roleName) || isStaffTableRole(roleName);
};

/** Resolve package label from active subscription payload. */
export const resolveSubscriptionPackageName = (subscription) => {
  if (!subscription) return "";
  const raw =
    subscription?.subscriptionType?.subType ||
    subscription?.subscriptionType?.name ||
    subscription?.subscriptionType?.typeName ||
    (typeof subscription?.subscriptionType === "string"
      ? subscription.subscriptionType
      : "") ||
    subscription?.packageName ||
    subscription?.subscriptionPackage ||
    "";
  return String(raw || "").toUpperCase().trim();
};

/** Attendance is Premium-only (not Basic / Intermediate). */
export const isPremiumPackageName = (packageName) =>
  String(packageName || "")
    .toUpperCase()
    .trim()
    .includes("PREMIUM");

/** True for Basic package only (Intermediate / Premium take precedence if both appear). */
export const isBasicPackageName = (packageName) => {
  const n = String(packageName || "")
    .toUpperCase()
    .trim();
  if (!n) return false;
  if (n.includes("PREMIUM") || n.includes("INTERMEDIATE")) return false;
  return n.includes("BASIC");
};

/**
 * Path / Pathology module is Intermediate + Premium only.
 * Basic package must not show Pathology Inventory, Pathology OPD/IPD, or related Pathology UI.
 */
export const packageIncludesPathModule = (packageName) => {
  const n = String(packageName || "")
    .toUpperCase()
    .trim();
  return n.includes("INTERMEDIATE") || n.includes("PREMIUM");
};

/**
 * Returns true only when org active subscription is PREMIUM.
 * Failures default to false so Basic/Intermediate never track attendance by mistake.
 */
export async function organizationHasPremiumAttendance(organizationId) {
  try {
    const orgId =
      organizationId ||
      getOrganizationId() ||
      Number(localStorage.getItem("organizationId")) ||
      null;
    if (!orgId) return false;
    const active = await subscriptionHistoryService.getActiveSubscription(orgId);
    return isPremiumPackageName(resolveSubscriptionPackageName(active));
  } catch (error) {
    console.warn("[attendanceFlow] Could not resolve package for attendance:", error);
    return false;
  }
}

const getTodayDate = () => format(new Date(), "yyyy-MM-dd");
const getNowTime = () => format(new Date(), "HH:mm");

async function resolveDoctorRecord(userId, mobile) {
  const orgId = localStorage.getItem("organizationId");
  if (orgId && mobile) {
    try {
      return await getDoctorByMobile(mobile, Number(orgId));
    } catch (error) {
      console.warn("[attendanceFlow] Doctor mobile lookup failed:", error);
    }
  }
  return getDoctorByUserId(userId, mobile || null);
}

async function resolveStaffRecord(userId, mobile) {
  const orgId = localStorage.getItem("organizationId");
  if (orgId && mobile) {
    try {
      return await getStaffByMobile(mobile, Number(orgId));
    } catch (error) {
      console.warn("[attendanceFlow] Staff mobile lookup failed:", error);
    }
  }
  return getStaffByUserId(userId, mobile || null);
}

/** Check-in for login. Never throws — failures are logged only. */
export async function performAttendanceCheckIn({ roleName, entityDetails }) {
  if (!shouldTrackAttendance(roleName) || !entityDetails?.id) {
    return null;
  }

  // Basic / Intermediate packages do not include attendance
  const premiumEnabled = await organizationHasPremiumAttendance();
  if (!premiumEnabled) {
    return null;
  }

  const isDoctor = isDoctorTableRole(roleName);
  const todayDate = getTodayDate();

  let attendanceRecord = null;
  try {
    attendanceRecord = isDoctor
      ? await getTodaysAttendanceByDoctorId(entityDetails.id)
      : await getTodaysAttendanceByStaffId(entityDetails.id);
  } catch (error) {
    if (error?.response?.status !== 404) {
      console.warn("[attendanceFlow] Could not fetch today attendance:", error);
      return null;
    }
  }

  const needsCheckIn =
    !attendanceRecord ||
    attendanceRecord.status !== "PRESENT" ||
    !attendanceRecord.checkInTime;

  if (!needsCheckIn) {
    return attendanceRecord;
  }

  try {
    return await createAttendance({
      ...(isDoctor ? { doctorId: entityDetails.id } : { staffId: entityDetails.id }),
      attendanceDate: todayDate,
      status: "PRESENT",
      checkInTime: getNowTime(),
      remarks: "present",
    });
  } catch (error) {
    console.warn("[attendanceFlow] Check-in failed:", error);
    return null;
  }
}

/** Check-out for logout. Never throws — failures are logged only. */
export async function performAttendanceCheckOut({ roleName, userId, mobile } = {}) {
  if (!shouldTrackAttendance(roleName) || !userId) {
    return null;
  }

  // Basic / Intermediate packages do not include attendance
  const premiumEnabled = await organizationHasPremiumAttendance();
  if (!premiumEnabled) {
    return null;
  }

  const isDoctor = isDoctorTableRole(roleName);
  let entity = null;

  try {
    entity = isDoctor
      ? await resolveDoctorRecord(userId, mobile)
      : await resolveStaffRecord(userId, mobile);
  } catch (error) {
    console.warn("[attendanceFlow] Could not resolve record for checkout:", error);
    return null;
  }

  if (!entity?.id) {
    return null;
  }

  let attendance = null;
  try {
    attendance = isDoctor
      ? await getTodaysAttendanceByDoctorId(entity.id)
      : await getTodaysAttendanceByStaffId(entity.id);
  } catch (error) {
    if (error?.response?.status !== 404) {
      console.warn("[attendanceFlow] Could not fetch attendance for checkout:", error);
    }
    return null;
  }

  if (!attendance || attendance.checkOutTime) {
    return attendance;
  }

  const attendanceId = attendance.id || attendance.attendanceId;
  if (!attendanceId) {
    return null;
  }

  const { id: _id, attendanceId: _attendanceId, ...attendanceData } = attendance;

  try {
    return await updateAttendance(attendanceId, {
      ...attendanceData,
      ...(isDoctor ? { doctorId: entity.id } : { staffId: entity.id }),
      attendanceDate: getTodayDate(),
      status: attendance.status || "PRESENT",
      checkOutTime: getNowTime(),
    });
  } catch (error) {
    console.warn("[attendanceFlow] Check-out failed:", error);
    return null;
  }
}
